import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "影视分类" },
  { to: "/channel", label: "频道推荐" },
  { to: "/about", label: "关于我们" },
];

export default function Header() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-background-200/60 bg-background-50/80 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-4 px-4 md:h-[68px] md:px-8">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="fa-solid fa-spade text-[18px]" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-xl font-600 tracking-wide text-foreground-950">
            黑桃视频
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-[15px] transition-colors ${
                  isActive
                    ? "text-primary-500"
                    : "text-foreground-700 hover:text-primary-400"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="hidden items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 lg:flex"
          role="search"
        >
          <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-[16px]" aria-hidden="true"></i>
          </span>
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影视作品"
            aria-label="搜索影视作品"
            className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none xl:w-52"
          />
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <div className="flex items-center gap-2 md:hidden">
          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-search-line text-[20px]" aria-hidden="true"></i>
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className={menuOpen ? "ri-close-line text-[22px]" : "ri-menu-line text-[22px]"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <nav className="flex flex-col px-4 py-3" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-[15px] transition-colors ${
                    isActive ? "bg-primary-500/10 text-primary-500" : "text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/search"
              onClick={() => setMenuOpen(false)}
              className="rounded-md px-3 py-3 text-[15px] text-foreground-800"
            >
              搜索
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}